import './mapControls.scss';
import maplibregl, { type Map as MapLibreMap, type StyleSpecification } from 'maplibre-gl';
import { useEffect, useEffectEvent, useMemo, useRef, useState, type ReactNode } from 'react';
import { MapContext } from './mapContext';
import { createInteractiveLayers } from './interactiveLayers';
import { flightDuration } from './flight';
import { createOverlayRegistry } from './overlays';
import type { MapView } from './urlView';
import { forwardMapWheel } from './wheelPassthrough';

interface Props {
  initialView: MapView;
  /** Called once when the map is created. */
  createStyle: () => StyleSpecification;
  children: ReactNode;
}

/** Owns the MapLibre instance; renders the map full-screen and exposes it to children via useMap(). */
export function MapProvider({ initialView, createStyle, children }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<MapLibreMap | null>(null);
  const [overlays] = useState(createOverlayRegistry);
  const [interactiveLayers] = useState(createInteractiveLayers);

  const createMap = useEffectEvent(
    (container: HTMLElement) =>
      new maplibregl.Map({
        container,
        center: [initialView.lng, initialView.lat],
        zoom: initialView.zoom,
        maxPitch: 75,
        style: createStyle(),
        attributionControl: { compact: true },
      }),
  );

  useEffect(() => {
    const instance = createMap(containerRef.current!);
    const stopWheelForwarding = forwardMapWheel(instance);
    // Touch screens pinch to zoom, so only the compass (rotation and pitch) is worth the space there.
    const touch = matchMedia('(pointer: coarse)').matches;
    instance.addControl(
      new maplibregl.NavigationControl({ visualizePitch: true, showZoom: !touch }),
      'top-right',
    );
    instance.addControl(createGeolocateControl(instance), 'top-right');
    instance.on('load', () => {
      // Start the compact attribution collapsed to its (i) button; it covers half a phone screen otherwise.
      instance
        .getContainer()
        .querySelector('.maplibregl-compact-show')
        ?.classList.remove('maplibregl-compact-show');
      setMap(instance);
    });
    return () => {
      setMap(null);
      stopWheelForwarding();
      instance.remove();
    };
  }, []);

  const value = useMemo(() => ({ map, overlays, interactiveLayers }), [map, overlays, interactiveLayers]);

  return (
    <MapContext value={value}>
      <div ref={containerRef} className="absolute inset-0" />
      {children}
    </MapContext>
  );
}

/**
 * The "my location" button. Its camera move gets the same duration as other flights: MapLibre's own
 * `_updateCamera` (an internal, see the maplibre-gl pin) fits the accuracy circle with flyTo's default speed.
 */
function createGeolocateControl(map: MapLibreMap) {
  const fitBoundsOptions = { maxZoom: 14 };
  const control = new maplibregl.GeolocateControl({
    positionOptions: { enableHighAccuracy: true },
    trackUserLocation: true,
    fitBoundsOptions,
  });
  control._updateCamera = (position) => {
    const center = new maplibregl.LngLat(position.coords.longitude, position.coords.latitude);
    const bounds = maplibregl.LngLatBounds.fromLngLat(center, position.coords.accuracy);
    const options = { ...fitBoundsOptions, bearing: map.getBearing() };
    const camera = map.cameraForBounds(bounds, options);
    const duration = camera?.center ? flightDuration(map, camera.center, camera.zoom) : undefined;
    // Tagged so the control doesn't take its own move for the user panning away.
    map.fitBounds(bounds, { ...options, duration }, { geolocateSource: true });
  };
  return control;
}
